import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import {
  getProfile, updateProfile, changePassword, deleteAccount,
} from '../services/userApi';
import SettingRow from '../components/ui/SettingRow';

function Account() {
  const {
    user, updateUser, logout, loading: authLoading,
  } = useContext(AuthContext);
  const {
    soundEnabled,
    toggleSound,
    showTimer,
    toggleShowTimer,
    showMoveCount,
    toggleShowMoveCount,
    showPreview,
    toggleShowPreview,
    confettiEnabled,
    toggleConfetti,
    theme,
    toggleTheme,
  } = useSettings();
  const navigate = useNavigate();

  // Profile form state
  const [profile, setProfile] = useState({
    username: '',
    firstName: '',
    lastName: '',
    displayName: '',
    avatar: '',
  });
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState({ type: '', text: '' });

  // Password form state
  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState({ type: '', text: '' });

  // Delete account state
  const [deletePassword, setDeletePassword] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState({ type: '', text: '' });

  // Redirect if not logged in (wait for auth to finish loading first)
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  // Load profile on mount
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const data = await getProfile();
        setProfile({
          username: data.username || '',
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          displayName: data.displayName || '',
          avatar: data.avatar || '',
        });
      } catch (err) {
        setProfileMessage({ type: 'error', text: err.message });
      } finally {
        setProfileLoading(false);
      }
    };

    if (user) {
      loadProfile();
    }
  }, [user]);

  // Handle profile form changes
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  // Handle avatar upload
  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setProfileMessage({ type: 'error', text: 'Please select an image file' });
      return;
    }

    // Allow larger raw files since we resize to 200px anyway
    if (file.size > 10 * 1024 * 1024) {
      setProfileMessage({ type: 'error', text: 'Please select an image smaller than 10MB' });
      return;
    }

    try {
      const bitmap = await createImageBitmap(file, {
        imageOrientation: 'from-image',
      });

      const canvas = document.createElement('canvas');
      const maxSize = 200;
      const scale = Math.min(maxSize / bitmap.width, maxSize / bitmap.height);
      canvas.width = bitmap.width * scale;
      canvas.height = bitmap.height * scale;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      bitmap.close();

      const base64 = canvas.toDataURL('image/jpeg', 0.85);

      // Safety check: processed avatar should be small (200px JPEG is typically 10-50KB)
      if (base64.length > 500 * 1024) {
        setProfileMessage({ type: 'error', text: 'Image processing failed. Please try a different image.' });
        return;
      }

      setProfile((prev) => ({ ...prev, avatar: base64 }));
    } catch (err) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setProfile((prev) => ({ ...prev, avatar: ev.target.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Save profile
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileSaving(true);
    setProfileMessage({ type: '', text: '' });

    try {
      const updated = await updateProfile(profile);
      updateUser(updated);
      setProfileMessage({ type: 'success', text: 'Profile updated successfully' });
    } catch (err) {
      setProfileMessage({ type: 'error', text: err.message });
    } finally {
      setProfileSaving(false);
    }
  };

  // Handle password form changes
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswords((prev) => ({ ...prev, [name]: value }));
  };

  // Change password
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordMessage({ type: '', text: '' });

    if (passwords.newPassword !== passwords.confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (passwords.newPassword.length < 6) {
      setPasswordMessage({ type: 'error', text: 'Password must be at least 6 characters' });
      return;
    }

    setPasswordSaving(true);

    try {
      await changePassword(passwords.currentPassword, passwords.newPassword);
      setPasswordMessage({ type: 'success', text: 'Password changed successfully' });
      setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setPasswordMessage({ type: 'error', text: err.message });
    } finally {
      setPasswordSaving(false);
    }
  };

  // Delete account
  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      setDeleteMessage({ type: 'error', text: 'Password is required' });
      return;
    }

    setDeleting(true);
    setDeleteMessage({ type: '', text: '' });

    try {
      await deleteAccount(deletePassword);
      logout();
      navigate('/');
    } catch (err) {
      setDeleteMessage({ type: 'error', text: err.message });
      setDeleting(false);
    }
  };

  if (authLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-puzzle-blue" />
      </div>
    );
  }

  if (profileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-puzzle-blue" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-puzzle-background py-8">
      <div className="max-w-2xl mx-auto px-4">
        <h1 className="text-2xl font-bold text-puzzle-text mb-8">Account Settings</h1>

        {/* Profile Section */}
        <section className="bg-puzzle-surface rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-puzzle-text mb-4">Profile</h2>

          <form onSubmit={handleProfileSubmit}>
            {/* Avatar */}
            <div className="mb-6 flex items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-puzzle-muted flex items-center justify-center overflow-hidden">
                {profile.avatar ? (
                  <img src={profile.avatar} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-2xl text-puzzle-text-secondary">
                    {user.email?.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div>
                <label
                  htmlFor="avatar-upload"
                  className="cursor-pointer text-puzzle-blue hover:opacity-80 font-medium"
                >
                  Change photo
                  <input
                    id="avatar-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarChange}
                    className="hidden"
                  />
                </label>
                {profile.avatar && (
                  <button
                    type="button"
                    onClick={() => setProfile((prev) => ({ ...prev, avatar: '' }))}
                    className="block text-sm text-puzzle-danger hover:opacity-80 mt-1"
                  >
                    Remove photo
                  </button>
                )}
              </div>
            </div>

            {/* Username */}
            <div className="mb-4">
              <label htmlFor="account-username" className="block text-sm font-medium text-puzzle-text mb-1">
                Username
                <input
                  id="account-username"
                  name="username"
                  type="text"
                  value={profile.username}
                  onChange={handleProfileChange}
                  minLength={3}
                  maxLength={30}
                  pattern="^[a-zA-Z0-9._]+$"
                  className="mt-1 w-full px-3 py-2 border border-puzzle-border rounded-lg focus:outline-none focus:ring-2 focus:ring-puzzle-blue font-normal bg-puzzle-surface text-puzzle-text"
                />
                <p className="mt-1 text-xs text-puzzle-text-secondary font-normal">Letters, numbers, dots, and underscores only</p>
              </label>
            </div>

            {/* Name fields */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="account-firstName" className="block text-sm font-medium text-puzzle-text mb-1">
                  First Name
                  <input
                    id="account-firstName"
                    name="firstName"
                    type="text"
                    value={profile.firstName}
                    onChange={handleProfileChange}
                    className="mt-1 w-full px-3 py-2 border border-puzzle-border rounded-lg focus:outline-none focus:ring-2 focus:ring-puzzle-blue font-normal bg-puzzle-surface text-puzzle-text"
                  />
                </label>
              </div>
              <div>
                <label htmlFor="account-lastName" className="block text-sm font-medium text-puzzle-text mb-1">
                  Last Name
                  <input
                    id="account-lastName"
                    name="lastName"
                    type="text"
                    value={profile.lastName}
                    onChange={handleProfileChange}
                    className="mt-1 w-full px-3 py-2 border border-puzzle-border rounded-lg focus:outline-none focus:ring-2 focus:ring-puzzle-blue font-normal bg-puzzle-surface text-puzzle-text"
                  />
                </label>
              </div>
            </div>

            {/* Display name */}
            <div className="mb-4">
              <label htmlFor="account-displayName" className="block text-sm font-medium text-puzzle-text mb-1">
                Display Name
                <input
                  id="account-displayName"
                  name="displayName"
                  type="text"
                  value={profile.displayName}
                  onChange={handleProfileChange}
                  className="mt-1 w-full px-3 py-2 border border-puzzle-border rounded-lg focus:outline-none focus:ring-2 focus:ring-puzzle-blue font-normal bg-puzzle-surface text-puzzle-text"
                />
              </label>
            </div>

            {/* Email (read-only) */}
            <div className="mb-4">
              <label htmlFor="account-email" className="block text-sm font-medium text-puzzle-text mb-1">
                Email
                <input
                  id="account-email"
                  type="email"
                  value={user.email}
                  disabled
                  className="mt-1 w-full px-3 py-2 border border-puzzle-border rounded-lg bg-puzzle-background text-puzzle-text-secondary font-normal"
                />
              </label>
            </div>

            {profileMessage.text && (
              <p className={`mb-4 text-sm ${profileMessage.type === 'error' ? 'text-puzzle-danger' : 'text-puzzle-success'}`}>
                {profileMessage.text}
              </p>
            )}

            <button
              type="submit"
              disabled={profileSaving}
              className="w-full bg-puzzle-blue text-white py-2 px-4 rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {profileSaving ? 'Saving...' : 'Save Profile'}
            </button>
          </form>
        </section>

        {/* Game Settings Section */}
        <section className="bg-puzzle-surface rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-puzzle-text mb-4">Game Settings</h2>

          {/* Display Settings */}
          <div className="bg-puzzle-background rounded-xl p-4">
            <h3 className="text-sm font-medium text-puzzle-text-secondary mb-2">Display</h3>
            <div className="divide-y divide-puzzle-border">
              <SettingRow
                icon={(
                  <svg className="w-5 h-5 text-puzzle-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                  </svg>
                )}
                label="Connection Sound"
                enabled={soundEnabled}
                onToggle={toggleSound}
              />
              <SettingRow
                icon={(
                  <svg className="w-5 h-5 text-puzzle-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
                label="Show Timer"
                enabled={showTimer}
                onToggle={toggleShowTimer}
              />
              <SettingRow
                icon={(
                  <svg className="w-5 h-5 text-puzzle-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" />
                  </svg>
                )}
                label="Show Move Count"
                enabled={showMoveCount}
                onToggle={toggleShowMoveCount}
              />
              <SettingRow
                icon={(
                  <svg className="w-5 h-5 text-puzzle-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                )}
                label="Show Preview Image"
                enabled={showPreview}
                onToggle={toggleShowPreview}
              />
            </div>
          </div>

          {/* Effects Settings */}
          <div className="bg-puzzle-background rounded-xl p-4 mt-4">
            <h3 className="text-sm font-medium text-puzzle-text-secondary mb-2">Effects</h3>
            <SettingRow
              icon={(
                <svg className="w-5 h-5 text-puzzle-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              )}
              label="Confetti on Completion"
              enabled={confettiEnabled}
              onToggle={toggleConfetti}
            />
          </div>

          {/* Appearance Settings */}
          <div className="bg-puzzle-background rounded-xl p-4 mt-4">
            <h3 className="text-sm font-medium text-puzzle-text-secondary mb-2">Appearance</h3>
            <SettingRow
              icon={(
                <svg className="w-5 h-5 text-puzzle-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
              label="Dark Mode"
              enabled={theme === 'dark'}
              onToggle={toggleTheme}
            />
          </div>
        </section>

        {/* Password Section */}
        <section className="bg-puzzle-surface rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-puzzle-text mb-4">Change Password</h2>

          <form onSubmit={handlePasswordSubmit}>
            <div className="mb-4">
              <label htmlFor="account-currentPassword" className="block text-sm font-medium text-puzzle-text mb-1">
                Current Password
                <input
                  id="account-currentPassword"
                  name="currentPassword"
                  type="password"
                  value={passwords.currentPassword}
                  onChange={handlePasswordChange}
                  required
                  className="mt-1 w-full px-3 py-2 border border-puzzle-border rounded-lg focus:outline-none focus:ring-2 focus:ring-puzzle-blue font-normal bg-puzzle-surface text-puzzle-text"
                />
              </label>
            </div>

            <div className="mb-4">
              <label htmlFor="account-newPassword" className="block text-sm font-medium text-puzzle-text mb-1">
                New Password
                <input
                  id="account-newPassword"
                  name="newPassword"
                  type="password"
                  value={passwords.newPassword}
                  onChange={handlePasswordChange}
                  required
                  minLength={6}
                  className="mt-1 w-full px-3 py-2 border border-puzzle-border rounded-lg focus:outline-none focus:ring-2 focus:ring-puzzle-blue font-normal bg-puzzle-surface text-puzzle-text"
                />
              </label>
            </div>

            <div className="mb-4">
              <label htmlFor="account-confirmPassword" className="block text-sm font-medium text-puzzle-text mb-1">
                Confirm New Password
                <input
                  id="account-confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={passwords.confirmPassword}
                  onChange={handlePasswordChange}
                  required
                  minLength={6}
                  className="mt-1 w-full px-3 py-2 border border-puzzle-border rounded-lg focus:outline-none focus:ring-2 focus:ring-puzzle-blue font-normal bg-puzzle-surface text-puzzle-text"
                />
              </label>
            </div>

            {passwordMessage.text && (
              <p className={`mb-4 text-sm ${passwordMessage.type === 'error' ? 'text-puzzle-danger' : 'text-puzzle-success'}`}>
                {passwordMessage.text}
              </p>
            )}

            <button
              type="submit"
              disabled={passwordSaving}
              className="w-full bg-puzzle-blue text-white py-2 px-4 rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {passwordSaving ? 'Changing...' : 'Change Password'}
            </button>
          </form>
        </section>

        {/* Danger Zone */}
        <section className="bg-puzzle-surface rounded-lg shadow p-6 border border-puzzle-danger/30">
          <h2 className="text-lg font-semibold text-puzzle-danger mb-4">Danger Zone</h2>

          {!showDeleteConfirm ? (
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              className="w-full bg-puzzle-danger text-white py-2 px-4 rounded-lg hover:opacity-90"
            >
              Delete Account
            </button>
          ) : (
            <div>
              <p className="text-puzzle-text-secondary mb-4">
                This action cannot be undone. Please enter your password to confirm.
              </p>

              <input
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder="Enter your password"
                className="w-full px-3 py-2 border border-puzzle-border rounded-lg focus:outline-none focus:ring-2 focus:ring-puzzle-danger mb-4 bg-puzzle-surface text-puzzle-text placeholder-puzzle-text-secondary"
              />

              {deleteMessage.text && (
                <p className={`mb-4 text-sm ${deleteMessage.type === 'error' ? 'text-puzzle-danger' : 'text-puzzle-success'}`}>
                  {deleteMessage.text}
                </p>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleDeleteAccount}
                  disabled={deleting}
                  className="flex-1 bg-puzzle-danger text-white py-2 px-4 rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleting ? 'Deleting...' : 'Delete My Account'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeletePassword('');
                    setDeleteMessage({ type: '', text: '' });
                  }}
                  className="flex-1 bg-puzzle-background text-puzzle-text py-2 px-4 rounded-lg hover:bg-puzzle-hover"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default Account;
